import { rem } from 'polished'
import React from 'react'
import type { FileError } from 'react-dropzone'
import { useIntl } from 'react-intl'
import styled from 'styled-components'

import { MAX_FILE_SIZE_MB } from './constants'

import TextButton from 'LuxKit/Button/TextButton'
import LinePaperclipIcon from 'LuxKit/Icons/line/LinePaperclipIcon'
import BodyText from 'LuxKit/Typography/BodyText'
import Caption from 'LuxKit/Typography/Caption'
import Group from 'components/utils/Group'
import type { UploadTripDocumentError } from 'tripPlanner/api/documents/types'

const Root = styled(Group)`
  border-radius: ${(props) => props.theme.borderRadius.S};
  padding: ${rem(12)};
  background-color: ${(props) =>
    props.theme.palette.messaging.critical.lightBackground};
`

const MainSection = styled.div`
  flex: 1;
`

interface Props {
  uploadError?: UploadTripDocumentError | null
  dropzoneError?: FileError | null
  onRequestRetry?: () => void
  onRequestDismiss?: () => void
}

function FileUploadError({
  uploadError,
  dropzoneError,
  onRequestRetry,
  onRequestDismiss,
}: Props) {
  const intl = useIntl()

  let errorMsg = intl.formatMessage({
    id: 'qx/iynSoiE',
    defaultMessage: 'Error occurred while uploading your file.',
    description: 'Generic error message when a document upload fails',
  })
  if (
    uploadError?.status === 422 &&
    uploadError.code === 'InvalidFileNameError'
  ) {
    errorMsg = intl.formatMessage({
      id: 'fDLG3dUe7F',
      defaultMessage: 'Invalid file name. Please rename your file and try again.',
      description: 'Error message when uploaded file has an invalid name',
    })
  } else if (dropzoneError) {
    if (dropzoneError.code === 'file-too-large') {
      errorMsg = intl.formatMessage(
        {
          id: 'Kn06sMmDCm',
          defaultMessage: 'File too large. Maximum size allowed is {maxSizeMB}MB.',
          description: 'Error message when uploaded file exceeds the maximum allowed size',
        },
        { maxSizeMB: MAX_FILE_SIZE_MB },
      )
    } else if (dropzoneError.code === 'file-invalid-type') {
      errorMsg = intl.formatMessage({
        id: 'vMQ5qhCs6o',
        defaultMessage: 'Invalid file. Upload only .PDF, .JPEG .PNG and .HEIC.',
        description: 'Error message when uploaded file type is not supported',
      })
    } else if (dropzoneError.code === 'invalid-file-name') {
      errorMsg = intl.formatMessage({
        id: 'vaIFtsozaY',
        defaultMessage: "Invalid file name. File names cannot contain '?', '#' or '%'. Please rename your file and try again.",
        description: 'Error message when file name contains forbidden characters',
      })
    }
  }
  return (
    <Root direction="horizontal" gap={12} verticalAlign="center" fullWidth>
      <LinePaperclipIcon size="XS" colour="neutral-two" />
      <MainSection>
        <BodyText variant="large" lineClamp={1}>
          {intl.formatMessage({
            id: 'sDOIEzSpVn',
            defaultMessage: 'Upload failed',
            description: 'Title shown when a document upload fails',
          })}
        </BodyText>
        <Caption variant="medium">{errorMsg}</Caption>
      </MainSection>
      {onRequestRetry && !!uploadError && (
        <TextButton kind="tertiary" onClick={onRequestRetry}>
          {intl.formatMessage({
            id: 'sCucIC/tU7',
            defaultMessage: 'Try again',
            description: 'Button to retry a failed document upload',
          })}
        </TextButton>
      )}
      {onRequestDismiss && (
        <TextButton kind="tertiary" onClick={onRequestDismiss}>
          {intl.formatMessage({
            id: 'K0KMjuCXnt',
            defaultMessage: 'Dismiss',
            description: 'Button to dismiss a document upload error',
          })}
        </TextButton>
      )}
    </Root>
  )
}

export default FileUploadError
